import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gift, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { redeemAppSumoCoupon } from '../lib/RedeemDataService';

const Redeem = () => {
  const { t } = useTranslation();
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // 'success', 'error', 'invalid'
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!couponCode.trim()) return;

    setLoading(true);
    setResult(null);
    setMessage('');

    try {
      const response = await redeemAppSumoCoupon(couponCode.trim());
      
      if (response.success) {
        setResult('success');
        setMessage(t('couponRedeemedMessage'));
        setCouponCode(''); // Clear the form on success
      } else {
        setResult('invalid');
        setMessage(response.message || t('invalidCouponMessage'));
      }
    } catch (error) {
      console.error('Redemption error:', error);
      
      if (error.status === 400 || error.message.includes('invalid') || error.message.includes('Invalid')) {
        setResult('invalid');
        setMessage(t('invalidCouponMessage'));
      } else {
        setResult('error');
        setMessage(t('redeemErrorMessage'));
      }
    } finally {
      setLoading(false);
    }
  };

  const getResultIcon = () => {
    switch (result) {
      case 'success':
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case 'invalid':
        return <XCircle className="h-6 w-6 text-red-500" />;
      case 'error':
        return <AlertCircle className="h-6 w-6 text-orange-500" />;
      default:
        return null;
    }
  };

  const getResultColor = () => {
    switch (result) {
      case 'success':
        return 'text-green-600';
      case 'invalid':
        return 'text-red-600';
      case 'error':
        return 'text-orange-600';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2 sm:gap-3">
          <Gift className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
          {t('redeemCoupon')}
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          {t('redeemCouponDescription')}
        </p>
      </div>

      {/* Form */}
      <Card className="w-full max-w-md">
          <CardHeader>
            <h3 className="text-lg font-medium text-gray-900">
              {t('couponCode')}
            </h3>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder={t('couponCodePlaceholder')}
                  className="w-full"
                  disabled={loading}
                  required
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading || !couponCode.trim()}
                className="w-full"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('redeeming')}
                  </>
                ) : (
                  t('redeem')
                )}
              </Button>
            </form>

            {/* Result Message */}
            {result && (
              <div className={`mt-4 p-4 rounded-md border ${getResultColor()} border-current`}>
                <div className="flex items-center">
                  {getResultIcon()}
                  <div className="ml-3">
                    <h4 className="text-sm font-medium">
                      {result === 'success' && t('couponRedeemed')}
                      {result === 'invalid' && t('invalidCoupon')}
                      {result === 'error' && t('redeemError')}
                    </h4>
                    <p className="text-sm mt-1">
                      {message}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
    </div>
  );
};

export default Redeem;
